import { Direction, Encoding, Status } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { SMS } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { DesktopSMSAlert } from '@weave/schema-gen-ts/dist/shared/notification/alerts.pb';
import { SmsNewMessageEventPayload } from '@frontend/websocket';
import { convertStringToStatus } from './convert-string-to-status';

const convertStringToAction = (action: string): SmsNewMessageEventPayload['params']['action'] => {
  switch (action.toLowerCase()) {
    case 'inbound':
      return 'inbound';
    case 'outbound':
      return 'outbound';
    case 'inboundthreadupdate':
      return 'inboundThreadUpdate';
    default:
      return 'inbound';
  }
};

const convertDesktopSMSAlertToWSPayload = (params: DesktopSMSAlert): SmsNewMessageEventPayload['params'] => {
  return {
    action: convertStringToAction(params.action || 'inbound'),
    body: params.body || '',
    department_id: params.departmentId || '',
    first_name: params.firstName || '',
    household_id: params.householdId || '',
    last_name: params.lastName || '',
    location_id: params.locationId || '',
    media_ids: params.mediaIds || [],
    media_url: params.mediaUrl || [],
    num_media: params.numMedia || 0,
    patient_id: params.patientId || '',
    patient_number: params.patientNumber || '',
    person_id: params.PersonID || '',
    phone_mobile: params.phoneMobile || '',
    received_at: params.receivedAt || '',
    related_ids: params.relatedIds || [],
    sms_id: params.smsId || '',
    status: params.status || '',
    string: params.string || '',
    thread_ids: params.threadIds || [],
    to: params.to || '',
    uuid: params.uuid || '',
    weave_number: params.weaveNumber || '',
    weave_user_id: params.weaveUserId || '',
    auto_generated_by: params.autoGeneratedBy || '',
    thread_status: params.threadStatus,
    process_silently: params.processSilently,
  };
};

export const convertWSPayloadToSMS = (params: SmsNewMessageEventPayload['params'] | DesktopSMSAlert): SMS => {
  const isOutbound = params.action === 'outbound';

  const normalizedParams =
    'locationId' in params
      ? convertDesktopSMSAlertToWSPayload(params)
      : (params as SmsNewMessageEventPayload['params']);

  return {
    // params.sms_id is actually the providerMsgId, and the uuid is the Weave sms id
    id: normalizedParams.uuid,
    providerMsgId: normalizedParams.sms_id,
    threadId:
      normalizedParams.thread_ids && Array.isArray(normalizedParams.thread_ids)
        ? normalizedParams.thread_ids[0] ?? ''
        : '',
    locationId: normalizedParams.location_id,
    locationPhone: normalizedParams.weave_number,
    personPhone: normalizedParams.patient_number,
    direction: isOutbound ? Direction.DIRECTION_OUTBOUND : Direction.DIRECTION_INBOUND,
    body: normalizedParams.body,
    status: convertStringToStatus(
      normalizedParams.thread_status || normalizedParams.status,
      isOutbound ? Status.STATUS_SENT : Status.STATUS_NEW
    ),
    statusDetails: '',
    numMedia: normalizedParams.num_media,
    media: normalizedParams.media_ids.map((id, index) => ({ mediaId: id, url: normalizedParams.media_url.at(index) })),
    encoding: Encoding.ENCODING_UNSPECIFIED,
    segments: 1,
    relatedIds: normalizedParams.related_ids ?? [],
    labels: {},
    programSlugId: '',
    personId: normalizedParams.person_id || normalizedParams.patient_id,
    deadline: '',
    autogeneratedBy: normalizedParams.auto_generated_by ?? '',
    readBy: '',
    deletedBy: '',
    createdBy: isOutbound ? normalizedParams.weave_user_id : normalizedParams.person_id || normalizedParams.patient_id,
    createdAt: normalizedParams.received_at || new Date().toISOString(),
    updatedBy: '',
    updatedAt: '',
    readAt: '',
    deletedAt: '',
    actionable: false,
    departmentId: normalizedParams.department_id,
    tags: [],
  };
};
